import { useContext, useEffect, useState } from "react";
import {
  FaCog,
  FaFileContract,
  FaImage,
  FaPalette,
  FaTrashAlt,
  FaUpload,
  FaSave,
  FaTimes,
  FaCheck,
  FaStar,
} from "react-icons/fa";
import { FILES_URL } from "../../../paths";
import { AuthContext } from "../../auth/auth-provider";
import Button from "../../ui/controls/button/button";
import CardBox from "../../ui/controls/card-box/card-box";
import ColorPicker from "../../ui/controls/color-picker/color-picker";
import FileField from "../../ui/controls/file-field/file-field";
import Pager from "../../ui/controls/pager/pager";
import TextField from "../../ui/controls/text-field/text-field";
import Column from "../../ui/layout/column/column";
import Row from "../../ui/layout/row/row";
import Modal from "../../ui/modal/modal";
import { ModalContext } from "../../ui/modal/modal-provider";
import "./settings.css";
import Dialogs from "components/ui/dialogs/dialogs";
import InfoMessage from "components/ui/controls/info-message/info-message";

const Settings = (props) => {
  // Initialize states
  const [visible, setVisible] = useState(props?.visible);
  const auth = useContext(AuthContext);
  const settings = auth.preferences;

  // On visible change
  useEffect(() => {
    setVisible(props?.visible);
  }, [props?.visible]);

  return visible ? (
    <Modal
      title="Préférences"
      icon={<FaStar />}
      width="85%"
      height="85%"
      visible={visible}
      onClose={() => {
        props?.onClose?.();
      }}
    >
      <ModalContext.Consumer>
        {(modal) => (
          <Pager
            withMenu
            pages={[
              {
                isTitle: true,
                title: "Personnalisation",
                subTitle: "Personnalisez les études",
              },
              {
                label: "Palette de couleurs",
                icon: <FaPalette />,
                hidden: !settings.peutChangerCouleurs,
                content: (
                  <Column tpad fill>
                    <div className="settings-modern-section">
                      <div className="settings-section-header">
                        <div className="settings-section-icon">
                          <FaPalette />
                        </div>
                        <div>
                          <h2 className="settings-section-title">
                            Palette de couleurs
                          </h2>
                          <p className="settings-section-subtitle">
                            Définissez l'identité visuelle de votre application
                          </p>
                        </div>
                      </div>

                      <div className="settings-color-grid">
                        <div className="settings-color-card">
                          <div
                            className="settings-color-preview"
                            style={{
                              backgroundColor:
                                settings.couleurs &&
                                settings.couleurs.length > 0
                                  ? settings.couleurs[0]
                                  : "#1E3C72",
                            }}
                          ></div>
                          <div className="settings-color-info">
                            <h3>Couleur principale</h3>
                            <p>
                              Utilisée pour les éléments d'interface principaux
                            </p>
                            <ColorPicker
                              value={
                                settings.couleurs &&
                                settings.couleurs.length > 0
                                  ? settings.couleurs[0]
                                  : "#1E3C72"
                              }
                              onChange={(evt) => {
                                auth.modifierPreferences("couleurs", [
                                  evt.target.value,
                                  settings.couleurs &&
                                  settings.couleurs.length > 1
                                    ? settings.couleurs[1]
                                    : "#2196F3",
                                ]);
                              }}
                              xsmall
                              inline
                            />
                          </div>
                        </div>

                        <div className="settings-color-card">
                          <div
                            className="settings-color-preview"
                            style={{
                              backgroundColor:
                                settings.couleurs &&
                                settings.couleurs.length > 1
                                  ? settings.couleurs[1]
                                  : "#2196F3",
                            }}
                          ></div>
                          <div className="settings-color-info">
                            <h3>Couleur secondaire</h3>
                            <p>
                              Utilisée pour les soustitres et éléments
                              secondaires
                            </p>
                            <ColorPicker
                              value={
                                settings.couleurs &&
                                settings.couleurs.length > 1
                                  ? settings.couleurs[1]
                                  : "#2196F3"
                              }
                              onChange={(evt) => {
                                auth.modifierPreferences("couleurs", [
                                  settings.couleurs &&
                                  settings.couleurs.length > 0
                                    ? settings.couleurs[0]
                                    : "#1E3C72",
                                  evt.target.value,
                                ]);
                              }}
                              xsmall
                              inline
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Column>
                ),
              },
              {
                label: "Logo",
                icon: <FaImage />,
                hidden: !settings.peutChangerLogo,
                content: (
                  <Column tpad fill>
                    <div className="settings-modern-section">
                      <div className="settings-section-header">
                        <div className="settings-section-icon">
                          <FaImage />
                        </div>
                        <div>
                          <h2 className="settings-section-title">Logo</h2>
                          <p className="settings-section-subtitle">
                            Personnalisez le logo pour vos documents d'études
                          </p>
                        </div>
                      </div>

                      <div className="settings-logo-center">
                        {settings.peutChangerLogo && (
                          <>
                            <div className="settings-logo-preview-card">
                              <div className="settings-logo-preview-header">
                                <h3>Aperçu du logo</h3>
                                <p>Logo qui apparaîtra dans vos documents</p>
                              </div>
                              <div className="settings-logo-preview-zone">
                                <img
                                  id="apercu-logo"
                                  src={
                                    FILES_URL + (settings.logo || "/empty.svg")
                                  }
                                  alt="Logo"
                                  className="settings-logo-image"
                                />
                              </div>
                            </div>

                            <div className="settings-logo-actions">
                              <FileField
                                id="logo"
                                name="logo"
                                accept="image/png,image/jpeg"
                                normal
                                onChange={(evt) => {
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    const apercuLogo =
                                      document.getElementById("apercu-logo");
                                    apercuLogo.src = reader.result;
                                  };
                                  reader.readAsDataURL(evt.target.files[0]);
                                  if (
                                    evt.target.files[0].type === "image/png" ||
                                    evt.target.files[0].type === "image/jpeg"
                                  )
                                    auth.modifierPreferences(
                                      "fichier_logo",
                                      evt.target.files[0]
                                    );
                                  else
                                    Dialogs.error(
                                      "Format d'image non reconnu",
                                      "Erreur de format"
                                    );
                                }}
                                className="settings-capsule-btn settings-capsule-primary"
                              >
                                <FaUpload /> Changer le logo
                              </FileField>
                              {settings.logo !== "" && (
                                <Button
                                  className="settings-capsule-btn settings-capsule-danger"
                                  title="Supprimer le logo"
                                  onClick={() => {
                                    auth.modifierPreferences("logo", "");
                                  }}
                                >
                                  <FaTrashAlt /> Supprimer
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </Column>
                ),
              },
              {
                label: "Mentions légales",
                icon: <FaFileContract />,
                hidden: !settings.peutChangerMentions,
                content: (
                  <Column tpad fill>
                    <div className="settings-modern-section">
                      <div className="settings-section-header">
                        <div className="settings-section-icon">
                          <FaFileContract />
                        </div>
                        <div>
                          <h2 className="settings-section-title">
                            Mentions légales
                          </h2>
                          <p className="settings-section-subtitle">
                            Définissez les informations légales de votre
                            organisation
                          </p>
                        </div>
                      </div>

                      <div className="settings-text-editor">
                        <TextField
                          label=""
                          dataType="string"
                          value={settings.mentions}
                          onChange={(evt) =>
                            auth.modifierPreferences(
                              "mentions",
                              evt.target.value
                            )
                          }
                          multiline
                          rows={12}
                          className="settings-mentions-field"
                          placeholder="Saisissez ici vos mentions légales..."
                        />
                        <InfoMessage text="Ces informations seront affichées dans les documents générés." />
                      </div>
                    </div>
                  </Column>
                ),
              },
            ]}
            page={
              settings.peutChangerCouleurs === true
                ? 1
                : settings.peutChangerLogo === true
                ? 2
                : 3
            }
            navButtons={{
              cancel: {
                label: "Annuler",
                onClick: () => {
                  modal.hide();
                  props?.onCancel?.();
                },
              },
              previous: {
              },
              next: {
              },
              validate: {
                label: "Valider",
                onClick: () => {
                  auth.sauverPreferences(() => {
                    modal.hide();
                    props?.onValidate?.();
                  });
                },
              },
            }}
          />
        )}
      </ModalContext.Consumer>
    </Modal>
  ) : (
    <></>
  );
};
export default Settings;
